import {
  adminHubService,
} from './admin-hub-service';
import { logsService } from './logs-service';

export type AdminNotification = {
  id: string;
  channel: 'inbox' | 'activity';
  type: 'moderation' | 'verification' | 'dispute' | 'finance' | 'booking' | 'system' | 'activity';
  priority: 'high' | 'medium' | 'low';
  title: string;
  body: string;
  createdAt: string;
  href?: string;
};

export const READ_KEY = 'bh-admin-notif-read';
export const DISMISS_KEY = 'bh-admin-notif-dismissed';

export function loadIdSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((x: unknown) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveIdSet(key: string, set: Set<string>) {
  localStorage.setItem(key, JSON.stringify(Array.from(set)));
}

function unwrapData<T>(res: unknown): T | null {
  if (!res || typeof res !== 'object') return null;
  const r = res as Record<string, unknown>;
  if (r.data && typeof r.data === 'object') return r.data as T;
  return res as T;
}

export async function aggregateAdminNotifications(): Promise<AdminNotification[]> {
  const items: AdminNotification[] = [];
  const now = new Date().toISOString();

  try {
    const raw = await adminHubService.getGlobalDashboard();
    const hub = unwrapData<{
      queues?: {
        pendingModeration?: number;
        pendingVerifications?: number;
        openDisputes?: number;
        todayBookings?: number;
      };
    }>(raw);
    const q = hub?.queues;
    if (q) {
      if (Number(q.pendingModeration) > 0) {
        items.push({
          id: 'queue-moderation',
          channel: 'inbox',
          type: 'moderation',
          priority: 'medium',
          title: 'Pending moderation',
          body: `${q.pendingModeration} item(s) awaiting review`,
          createdAt: now,
          href: '/admin/moderation',
        });
      }
      if (Number(q.pendingVerifications) > 0) {
        items.push({
          id: 'queue-verification',
          channel: 'inbox',
          type: 'verification',
          priority: 'high',
          title: 'Verification queue',
          body: `${q.pendingVerifications} verification(s) pending`,
          createdAt: now,
          href: '/admin/verification-queue',
        });
      }
      if (Number(q.openDisputes) > 0) {
        items.push({
          id: 'queue-disputes',
          channel: 'inbox',
          type: 'dispute',
          priority: 'high',
          title: 'Open disputes',
          body: `${q.openDisputes} open dispute(s)`,
          createdAt: now,
          href: '/admin/disputes',
        });
      }
      if (Number(q.todayBookings) > 0) {
        items.push({
          id: 'queue-bookings-today',
          channel: 'inbox',
          type: 'booking',
          priority: 'low',
          title: 'Bookings today',
          body: `${q.todayBookings} booking(s) today`,
          createdAt: now,
          href: '/admin/analytics',
        });
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const logs = await logsService.getActivityLogs({ page: 1, limit: 8 });
    const rows = Array.isArray(logs?.data) ? logs.data : [];
    rows.slice(0, 8).forEach((row) => {
      items.push({
        id: `activity-${row.id}`,
        channel: 'activity',
        type: 'activity',
        priority: 'low',
        title: row.action || 'Activity',
        body: row.details || `${row.entityType} ${row.entityId}`.trim() || 'Recent activity',
        createdAt: row.createdAt || now,
        href: '/admin/logs',
      });
    });
  } catch {
    /* optional */
  }

  const priorityRank = { high: 0, medium: 1, low: 2 } as const;
  return items.sort((a, b) => {
    const pr = priorityRank[a.priority] - priorityRank[b.priority];
    if (pr !== 0) return pr;
    return String(b.createdAt).localeCompare(String(a.createdAt));
  });
}
